import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import { CreditCardDtoCollection, getSavedCreditCards } from "@NA/Services/SavedCreditCardsService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import {
    CreditCardPopupModel,
    CreditcardPopupStatusUpdateParameter,
    GetCreditCardPopupStatusParameter,
    TokenizationDisplayInfo,
} from "@NA/Widgets/CCTokenization/ADICCTokenizationModel";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";

import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import ADICCTokenizationModal from "@NA/Widgets/CCTokenization/ADICCTokenizationModal";
import { getCreditCardPopupStatus, updateCreditCardStatus } from "./ADICCTokenizationService";
import { convertCardTypeToName } from "@NA/Utils/Utility";

const mapStateToProps = (state: CustomApplicationState) => ({
    session: state.context.session,
    settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
    userRoles: state.ADIGlobalData.ADIUserRoleStore,
});

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

const ADICCTokenization: FC<Props> = ({ session, settingsCollection, userRoles }) => {
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const isEnableCCPopUp = settingsCollection && settingsCollection?.ccPoupSettingSettings.enableCCPopup;
    const [modalOpen, setModalOpen] = useState(false);
    const currentState = useSelector((state: CustomApplicationState) => state);
    const cart = getCurrentCartState(currentState);
    const billToState = getBillToState(currentState, cart?.value?.billToId);
    const closeModal = () => setModalOpen(false);
    const [creditCardCollection, setCreditCardCollection] = useState<CreditCardDtoCollection[]>([]);
    const customerNumberAndSuffix = billToState?.value?.customerNumber.toString().split("-") ?? [];
    const [customerData, setCustomerData] = useState<TokenizationDisplayInfo>({});
    const hasFetchedRef = useRef(false);
    
    useEffect(() => {
        if (hasFetchedRef.current) return;
        const isUserhavingfullaccess = userRoles && userRoles.fullAccess;
        if (isAuthenticated && userRoles && billToState?.value && isEnableCCPopUp && isUserhavingfullaccess) {
            fetchTokenizationStatus();
            hasFetchedRef.current = true;
        }
    }, [isAuthenticated, billToState?.value, isEnableCCPopUp]);

    const fetchTokenizationStatus = async () => {
        const params: GetCreditCardPopupStatusParameter = {
            webuseremail: session && session.email,
            customerNbr: customerNumberAndSuffix[0],
            customerSuffix: customerNumberAndSuffix[1],
        };
        try {
            const popUpStatus = await getCreditCardPopupStatus(params);
            if (popUpStatus.returnCode === "00") {
                fetchCreditCardInformation();
            }
        } catch (err) {
            console.info(err, "err");
        }
    };

    const fetchCreditCardInformation = async () => {
        try {
            const creditCardList = await getSavedCreditCards({ page: 1, pageSize: 100, contains: "" });

            if (creditCardList.creditCardDtoCollection.length) {
                setCreditCardCollection(creditCardList.creditCardDtoCollection);
                setCustomerData({
                    userName: session.userName,
                    customerName: billToState.value?.customerName,
                    accountNumber: billToState.value?.customerNumber,
                    dateSigned: new Date().toLocaleDateString("en-US"),
                    cardList: creditCardList.creditCardDtoCollection,
                });
                setModalOpen(true);
            }
        } catch (err) {
            console.info(err, "err");
        }
    };
    const updateCustomerStatus = async (selectedStatus: boolean) => {
        try {
            const creditCardPopupModels: CreditCardPopupModel[] = creditCardCollection.map(dto => {
                return {
                    customerNbr: customerNumberAndSuffix[0],
                    customerSuffix: customerNumberAndSuffix[1],
                    customerName: billToState.value?.customerName,
                    webUserFirstName: session.firstName,
                    webUserLastName: session.lastName,
                    webUserEmail: session.email,
                    tokenCC: dto.cardToken,
                    cardLastCC: dto.cardNumber.slice(-4),
                    cardTypeCC: dto.cardType,
                    expiryMonth: dto.expiryMonth,
                    expiryYear: dto.expiryYear,
                    optionYN: selectedStatus ? "Y" : "N",
                    cardTypeDesc: convertCardTypeToName(dto.cardType),
                };
            });

            const requestObject: CreditcardPopupStatusUpdateParameter = {
                creditCardData: creditCardPopupModels,
            };
            closeModal();
            await updateCreditCardStatus(requestObject);
        } catch (err) {
            console.info(err);
        }
    };

    const handleSelection = (selectedStatus: boolean) => {
        updateCustomerStatus(selectedStatus);
    };

    return (
        <ADICCTokenizationModal
            modalOpen={modalOpen}
            customerData={customerData}
            handleResponse={(status: boolean) => handleSelection(status)}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ADICCTokenization),
    definition: {
        displayName: "CC Token",
        icon: "branch",
        fieldDefinitions: [],
        group: "Common",
    },
};

export default widgetModule;
